/**
 * El carrito.
 *
 * Vive ENTERO en el navegador. No hay pedido en la base, no hay endpoint de
 * escritura público y no se consulta D1 una sola vez de más: el sitio ya pide
 * `/api/catalog`, y de ahí salen tanto los productos como los ajustes de la
 * promoción. El "carrito" es una lista de `{id, qty}` en `localStorage`.
 *
 * Decisión central: **se guardan ids y cantidades, nunca precios.** Un carrito
 * que guarda el precio del día que se armó le cotiza a la clienta un precio que
 * ella ya cambió, y nadie se entera hasta que reclaman. Acá los precios se
 * resuelven contra el catálogo vivo en cada render; si un producto subió,
 * desapareció o se agotó, el carrito se entera solo.
 *
 * Todo este módulo es puro salvo `leer`/`guardar`. La matemática se prueba sin
 * navegador.
 */

import { formatearCorto } from './precio';

/** Tope de productos DISTINTOS. El mensaje de WhatsApp viaja en la URL: un
 *  carrito sin techo genera un link que algunos clientes de WhatsApp truncan, y
 *  el pedido llegaría cortado a la mitad sin que nadie lo note. */
export const LIMITE_PRODUCTOS = 15;

/** Tope de piezas por producto. 99 es holgado para venta al menudeo y frena el
 *  dedazo de quien deja el dedo en el botón. */
export const MAX_POR_PRODUCTO = 99;

const CLAVE = 'odyscents.carrito.v1';

/**
 * Quién puede entrar al carrito.
 *
 * Sin precio no hay matemática que hacer, y un producto agotado no se puede
 * entregar. Los dos conservan su botón de consulta por WhatsApp: no se pierde la
 * venta, se saca de la calculadora.
 */
export function puedeAgregarse(producto) {
  return !!producto && producto.priceCents != null && !producto.agotado;
}

// --- operaciones sobre las líneas (inmutables) -------------------------------

export function agregar(lineas, id, cantidad = 1) {
  const actual = lineas.find((l) => l.id === id);
  if (actual) return cambiarCantidad(lineas, id, actual.qty + cantidad);
  if (lineas.length >= LIMITE_PRODUCTOS) return lineas;
  return [...lineas, { id, qty: Math.min(Math.max(1, cantidad), MAX_POR_PRODUCTO) }];
}

export function quitar(lineas, id) {
  return lineas.filter((l) => l.id !== id);
}

/** Bajar a 0 o menos saca el producto: es lo que espera quien toca "−" hasta el
 *  final, y evita líneas fantasma en el mensaje. */
export function cambiarCantidad(lineas, id, cantidad) {
  const n = Math.min(Math.floor(cantidad), MAX_POR_PRODUCTO);
  if (!Number.isFinite(n) || n <= 0) return quitar(lineas, id);
  return lineas.map((l) => (l.id === id ? { ...l, qty: n } : l));
}

export function vaciar() {
  return [];
}

// --- resolución contra el catálogo vivo --------------------------------------

/**
 * Cruza las líneas guardadas con el catálogo actual.
 *
 * Descarta en silencio lo que ya no se puede vender (producto borrado, ocultado,
 * agotado o al que le quitaron el precio). Silencio a propósito: el visitante no
 * tiene nada que resolver ahí, y un cartel de error sobre algo que decidió la
 * dueña hace tres días sólo lo asusta. Lo que sí importa —que no pague por algo
 * que no llega— queda garantizado.
 */
export function resolver(lineas, productos) {
  const porId = new Map((productos || []).map((p) => [p.id, p]));
  return (lineas || []).flatMap((l) => {
    const producto = porId.get(l.id);
    if (!puedeAgregarse(producto)) return [];
    const qty = Math.min(Math.max(1, Math.floor(l.qty)), MAX_POR_PRODUCTO);
    return [{ producto, qty, subtotalCents: producto.priceCents * qty }];
  });
}

/**
 * Tramos usables, ordenados de menos a más piezas. Descarta lo que venga torcido en
 * vez de propagar `undefined` a la aritmética.
 */
export function tramosValidos(ajustes = {}) {
  if (!ajustes.promoEnabled) return [];
  const lista = Array.isArray(ajustes.promoTiers) && ajustes.promoTiers.length
    ? ajustes.promoTiers
    // Respaldo para el JSON viejo de un solo tramo (o un bundle que todavía manda
    // los campos sueltos): un descuento que desaparece por un cambio de formato
    // sería peor que uno de un solo escalón.
    : [{ minUnits: ajustes.promoMinUnits, percent: ajustes.promoPercent }];
  return lista
    .filter((t) => t && Number.isInteger(t.minUnits) && Number.isInteger(t.percent) && t.percent > 0)
    .sort((a, b) => a.minUnits - b.minUnits);
}

/**
 * El mejor tramo para esa cantidad de piezas.
 *
 * Se elige por **mayor porcentaje** entre los que califican, no por "el último que
 * alcanzó". Normalmente da lo mismo porque la escritura exige que a más piezas haya
 * más descuento, pero si un dato viejo o torcido rompiera esa regla, el cliente que
 * compró MÁS no puede terminar pagando más. Ante duda, gana quien compra.
 */
export function mejorTramo(tramos, unidades) {
  let mejor = null;
  for (const t of tramos) {
    if (unidades < t.minUnits) continue;
    if (!mejor || t.percent > mejor.percent) mejor = t;
  }
  return mejor;
}

/** El primer tramo que todavía no alcanzó, para poder decirle cuánto le falta. */
export function proximoTramo(tramos, unidades) {
  const aplicado = mejorTramo(tramos, unidades);
  for (const t of tramos) {
    if (t.minUnits <= unidades) continue;
    if (aplicado && t.percent <= aplicado.percent) continue; // subir no le daría nada
    return t;
  }
  return null;
}

/**
 * El total, con la promoción de la dueña aplicada.
 *
 * El descuento se calcula **sobre el subtotal**, no línea por línea: sumar
 * redondeos por línea da un total que no cuadra con el que cualquiera saca con
 * una calculadora, y eso se discute por WhatsApp.
 *
 * Se redondea **hacia abajo al peso**. Hacia arriba sería cobrarle de más al
 * cliente por un decimal; hacia abajo son centavos que regala el negocio y que
 * nadie reclama.
 */
export function calcular(lineas, productos, ajustes = {}) {
  const items = resolver(lineas, productos);
  const unidades = items.reduce((n, i) => n + i.qty, 0);
  const subtotalCents = items.reduce((n, i) => n + i.subtotalCents, 0);

  const tramos = tramosValidos(ajustes);
  const tramo = mejorTramo(tramos, unidades);
  const proximo = unidades > 0 ? proximoTramo(tramos, unidades) : null;

  const descuentoCents = tramo
    ? Math.floor((subtotalCents * tramo.percent) / 100 / 100) * 100
    : 0;

  return {
    items,
    unidades,
    subtotalCents,
    descuentoCents,
    totalCents: subtotalCents - descuentoCents,
    promoAplicada: !!tramo && descuentoCents > 0,
    promoDisponible: tramos.length > 0,
    tramos,
    /** El tramo que se está aplicando, o null. */
    tramo,
    promoPercent: tramo ? tramo.percent : 0,
    /** Qué se gana subiendo, y cuánto falta. null si ya está en el tope. */
    proximo: proximo ? { ...proximo, faltan: proximo.minUnits - unidades } : null,
    /** Compatibilidad: cuántas piezas faltan para el próximo escalón. */
    faltanUnidades: proximo ? proximo.minUnits - unidades : 0,
  };
}

// --- el mensaje --------------------------------------------------------------

/**
 * Arma el texto que se manda por WhatsApp.
 *
 * ⚠️ Esto es un **resumen para abrir la conversación, no un pedido en firme**: se
 * construye en el navegador y quien lo manda puede editarlo antes de enviar. La
 * dueña cotiza contra su panel antes de cobrar. Queda escrito acá para que no se
 * descubra el día que alguien cambie el total a mano.
 */
export function mensajeWhatsApp(resumen) {
  const { items, subtotalCents, descuentoCents, totalCents, promoPercent } = resumen;
  if (!items.length) return '¡Hola Odyscents! Quiero hacer un pedido.';

  const lineas = items.map(
    (i, n) =>
      `${n + 1}. ${i.producto.brand} · ${i.producto.name}` +
      (i.qty > 1 ? ` × ${i.qty}` : '') +
      ` — ${formatearCorto(i.subtotalCents)}`
  );

  const partes = [
    '¡Hola Odyscents! Quiero estos perfumes:',
    '',
    ...lineas,
    '',
  ];

  // El subtotal sólo aparece cuando hay descuento: sin él, "subtotal" y "total"
  // serían el mismo número dos veces y el mensaje se lee peor.
  if (descuentoCents > 0) {
    partes.push(`Subtotal: ${formatearCorto(subtotalCents)}`);
    partes.push(`Descuento ${promoPercent}%: −${formatearCorto(descuentoCents)}`);
  }
  partes.push(`Total: ${formatearCorto(totalCents)} MXN`);
  partes.push('', '¿Cómo procedo con el pago?');

  return partes.join('\n');
}

// --- persistencia ------------------------------------------------------------

/** Lee el carrito guardado. Ante cualquier basura devuelve vacío: un carrito
 *  corrupto no debe impedir entrar al sitio. */
export function leer() {
  try {
    const crudo = window.localStorage.getItem(CLAVE);
    if (!crudo) return [];
    const datos = JSON.parse(crudo);
    if (!Array.isArray(datos)) return [];
    return datos
      .filter((l) => l && typeof l.id === 'string' && Number.isFinite(l.qty) && l.qty > 0)
      .slice(0, LIMITE_PRODUCTOS)
      .map((l) => ({ id: l.id, qty: Math.min(Math.floor(l.qty), MAX_POR_PRODUCTO) }));
  } catch {
    return [];
  }
}

/** Guarda. Falla en silencio: en modo privado de Safari `setItem` tira, y perder
 *  la persistencia del carrito no justifica romper la página. */
export function guardar(lineas) {
  try {
    window.localStorage.setItem(CLAVE, JSON.stringify(lineas));
  } catch {
    /* sin persistencia, el carrito sigue funcionando en memoria */
  }
}
